<template>
  <div>
    <v-container fluid class="py-0">
      <ERow>
        <ECol cols="6" class="pl-0 pb-0 text-h6">
          {{ tableTitle }}
        </ECol>
        <v-spacer />
      </ERow>

      <ERow>
        <ECol :cols="companyId ? '5' : '8'" class="px-0 pb-0">
          <v-select
            v-model="selectedEventPages"
            class="pr-4"
            :items="eventPages"
            dense
            outlined
            multiple
            chips
            small-chips
            deletable-chips
            hide-details
            label="Features White List"
            placeholder="Features White List"
            item-value="value"
            item-text="text"
            @change="fetchPosthogEventsStats"
          >
            <template #prepend-item>
              <v-list-item ripple @click="toggleSelectAllselectedEventPages">
                <v-list-item-action>
                  <v-checkbox
                    :input-value="isAllSelectedEventPages"
                    :indeterminate="isSomeSelectedEventPages"
                  ></v-checkbox>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>Select All</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider></v-divider>
            </template>

            <template #selection="{ item, index }">
              <v-chip v-if="index < maxVisibleChips" small class="text-caption">
                <span>{{ item.text }}</span>
              </v-chip>
              <span
                v-if="
                  isOverflowingNumberOfChips && index === maxVisibleChips + 1
                "
                dense
                class="grey--text text-caption"
              >
                (+{{ selectedEventPages.length - 1 }} more)
              </span>
            </template>
          </v-select>
        </ECol>
        <ECol
          v-if="companyId && !projectExid"
          cols="auto"
          md="3"
          sm="12"
          class="pl-0"
        >
          <CompanyProjectSelect
            :solo="false"
            :company-id="companyId"
            @change="onChangeSelectedProject"
          />
        </ECol>
        <ECol v-if="projectExid" cols="auto" md="3" sm="12" class="pl-0">
          <ProjectCameraSelect
            :solo="false"
            :project-id="projectExid"
            @change="onChangeSelectedCamera"
          />
        </ECol>
      </ERow>

      <ERow>
        <ECol cols="2" class="px-0 mr-2">
          <v-select
            v-model="selectedDateRange"
            :items="dateRanges"
            item-text="range"
            item-value="value"
            label="Range"
            hide-details
            outlined
            dense
            @change="onChangeSelectedDateRange"
          >
          </v-select>
        </ECol>
        <ECol
          v-if="selectedDateRange == DateRanges.Custom"
          cols="2"
          class="px-0 mr-2"
        >
          <v-menu v-model="startEndDatePicker" :close-on-content-click="false">
            <template #activator="{ on, attrs }">
              <v-text-field
                :value="formattedFromToDate"
                clearable
                outlined
                dense
                label="From / To"
                placeholder="From - To"
                readonly
                hide-details
                v-bind="attrs"
                v-on="on"
                @click:clear="
                  () => {
                    startDate = null
                    endDate = null
                  }
                "
              />
            </template>
            <v-date-picker
              v-model="startEndDates"
              range
              :max="$moment().format('YYYY-MM-DD')"
              max-width="290"
              @change="onChangeStartEndDates"
            />
          </v-menu>
        </ECol>

        <ECol
          cols="auto"
          md="3"
          sm="12"
          class="pl-1 pr-0 pb-0"
          :class="periodFilterColClass"
        >
          <v-select
            v-model="selectedPeriod"
            :items="periods"
            dense
            outlined
            label="Group by"
            hide-details
            item-value="value"
            item-text="text"
            @change="fetchPosthogEventsStats"
          >
            <template #item="{ item, attrs, on }">
              <v-list-item
                :disabled="
                  isDateRangeOver30Days() &&
                  item.value === Periods.Daily.toLowerCase()
                "
                v-bind="attrs"
                v-on="on"
              >
                <v-list-item-content>
                  <v-list-item-title>
                    {{ item.text }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-select>
        </ECol>

        <ECol class="flex-grow-1"></ECol>
        <ECol cols="1" class="d-flex align-center justify-end">
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                color="primary"
                v-bind="attrs"
                v-on="on"
                @click="isGraphView = !isGraphView"
              >
                <v-icon>{{
                  isGraphView ? "fas fa-table" : "fas fa-chart-simple"
                }}</v-icon>
              </v-btn>
            </template>
            {{ isGraphView ? "Switch to table view" : "Switch to graph view" }}
          </v-tooltip>
        </ECol>
      </ERow>

      <ERow>
        <ECol cols="12" class="pl-0">
          <PosthogEventsStatsTable
            v-if="!isGraphView"
            :title="tableTitle"
            :headers="headers"
            :items="items"
            :loading="loading"
            :options.sync="options"
            :total="total"
            :height="`${dataComponentHeight}px`"
          >
            <template #item.page="{ item }">
              {{
                item.page.startsWith("$") ? item.page.substring(1) : item.page
              }}
            </template>
          </PosthogEventsStatsTable>

          <PosthogEventsStatsChart
            v-else
            :is-loading="loading"
            :chart-data="chartData"
            :height="`${dataComponentHeight + 35}px`"
          />
        </ECol>
      </ERow>
    </v-container>
  </div>
</template>

<script>
import { debounce } from "@evercam/ui"
import { EvercamLabsApi } from "@evercam/shared/api/evercamLabsApi"
import { EventPagesList } from "@evercam/shared/constants/posthog"
import {
  DateRanges,
  Periods,
  PosthogEventsStatsTableHeaders,
} from "@evercam/shared/constants/posthog"

import CompanyProjectSelect from "@evercam/shared/components/CompanyProjectSelect"
import PosthogEventsStatsChart from "@evercam/shared/components/posthog/PosthogEventsStatsChart"
import PosthogEventsStatsTable from "@evercam/shared/components/posthog/PosthogEventsStatsTable"
import ProjectCameraSelect from "@evercam/shared/components/ProjectCameraSelect"

export default {
  name: "PosthogEventsStats",
  components: {
    CompanyProjectSelect,
    PosthogEventsStatsChart,
    PosthogEventsStatsTable,
    ProjectCameraSelect,
  },
  props: {
    companyExid: {
      type: String,
      default: "",
    },
    companyId: {
      type: [Number, null],
      default: null,
    },
    projectExid: {
      type: String,
      default: "",
    },
    email: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      dataComponentHeight: 500,
      dateRanges: [
        { range: "Last 24h", value: DateRanges.Last24h },
        { range: "Last 7 Days", value: DateRanges.Last7Days },
        { range: "Last 30 Days", value: DateRanges.Last30Days },
        { range: "Last 90 Days", value: DateRanges.Last90Days },
        { range: "Custom", value: DateRanges.Custom },
      ],
      startEndDatePicker: false,
      startEndDates: [],
      selectedDateRange: DateRanges.Last30Days,
      isGraphView: true,
      endDate: this.$moment().format("YYYY-MM-DD"),
      endDatePicker: false,
      chartData: {
        highchartsLabels: [],
        datasets: [],
      },
      headers: PosthogEventsStatsTableHeaders(this),
      baseHeaders: PosthogEventsStatsTableHeaders(this),
      items: [],
      loading: false,
      maxVisibleChips: 4,
      options: {
        sortBy: ["eventsCount"],
        sortDesc: [true],
      },
      periods: [
        {
          value: Periods.Daily.toLowerCase(),
          text: Periods.Daily,
        },
        {
          value: Periods.Weekly.toLowerCase(),
          text: Periods.Weekly,
        },
        {
          value: Periods.Monthly.toLowerCase(),
          text: Periods.Monthly,
        },
      ],
      selectedPeriod: Periods.Daily.toLowerCase(),
      selectedCameraExid: null,
      selectedProjectExid: null,
      startDate: this.$moment().subtract(30, "d").format("YYYY-MM-DD"),
      startDatePicker: false,
      selectedEventPages: [
        "LiveView",
        "Recordings",
        "Compare",
        "360",
        "Drone",
        "Bim",
        "Weather",
      ],
      total: 0,
    }
  },
  computed: {
    DateRanges() {
      return DateRanges
    },
    Periods() {
      return Periods
    },
    eventPages() {
      return EventPagesList
    },
    formattedEndDate() {
      return this.endDate ? this.formatDate(this.endDate, "DD MMM YYYY") : ""
    },
    formattedFromToDate() {
      if (!this.startDate && !this.endDate) {
        return ""
      }
      const fromDate = this.$moment(this.startDate)
      const toDate = this.$moment(this.endDate)

      const isSameYear = fromDate.year() === toDate.year()
      const isSameMonth = fromDate.month() === toDate.month()

      let fromDateFormat = "DD MMM YYYY"
      let toDateFormat = "DD MMM YYYY"
      if (isSameYear && isSameMonth) {
        fromDateFormat = "DD"
        toDateFormat = "DD MMM, YYYY"
      } else if (isSameYear) {
        fromDateFormat = "DD MMM"
        toDateFormat = "DD MMM, YYYY"
      }

      return `${this.formatDate(
        this.startDate,
        fromDateFormat
      )} - ${this.formatDate(this.endDate, toDateFormat)}`
    },
    formattedStartDate() {
      return this.startDate
        ? this.formatDate(this.startDate, "DD MMM YYYY")
        : ""
    },
    isAllSelectedEventPages() {
      return this.selectedEventPages.length === this.eventPages.length
    },
    isOverflowingNumberOfChips() {
      return this.selectedEventPages.length > this.maxVisibleChips
    },
    isSomeSelectedEventPages() {
      return (
        this.selectedEventPages.length > 0 &&
        this.selectedEventPages.length < this.eventPages.length
      )
    },
    periodFilterColClass() {
      return { "px-0": this.$vuetify.breakpoint.smAndDown }
    },
    tableTitle() {
      const titleMapping = {
        [Periods.Daily.toLowerCase()]: "Daily",
        [Periods.Weekly.toLowerCase()]: "Weekly",
        [Periods.Monthly.toLowerCase()]: "Monthly",
      }

      return `${titleMapping[this.selectedPeriod]} Features Stats`
    },
  },
  watch: {
    options(value, oldValue) {
      let isRefreshingTable = false

      // Check if 'paging' has changed
      if (
        value.page !== oldValue.page ||
        value.itemsPerPage !== oldValue.itemsPerPage
      ) {
        isRefreshingTable = true
      }
      // Check if 'sort' has changed && is a valid sort option
      const sortOptions = ["page", "eventsCount"]
      if (
        (value?.sortBy !== oldValue?.sortBy ||
          value?.sortDesc !== oldValue?.sortDesc) &&
        sortOptions.includes(value?.sortBy[0])
      ) {
        isRefreshingTable = true
      }
      if (!isRefreshingTable) {
        return
      }
      this.fetchPosthogEventsStats()
    },
  },
  async mounted() {
    await this.fetchPosthogEventsStats()
  },
  methods: {
    fetchPosthogEventsStats: debounce(async function () {
      if (
        (!this.email &&
          !this.companyExid &&
          !this.projectExid &&
          !this.selectedProjectExid) ||
        this.selectedEventPages.length == 0
      ) {
        // clear chart
        this.chartData = {
          highchartsLabels: [],
          datasets: null,
        }

        // clear table
        this.headers = [...this.baseHeaders]
        this.items = []
        this.total = 0

        return
      }

      const sortOptionsMapping = {
        page: "eventPageId",
        eventsCount: "eventsCount",
      }
      const sortBy = sortOptionsMapping[this.options.sortBy[0]]
      const sortDesc = this.options.sortDesc[0]
      const fullEndDate = this.$moment(this.endDate)
        .add(1, "d")
        .format("YYYY-MM-DDTHH:mm:ss")

      const params = {
        companyExid: this.companyExid,
        projectExid: this.projectExid ?? this.selectedProjectExid,
        cameraExid: this.selectedCameraExid,
        email: this.email,
        before: this.endDate ? fullEndDate : "",
        after: this.startDate
          ? this.$moment(this.startDate).format("YYYY-MM-DDTHH:mm:ss")
          : "",
        period: this.selectedPeriod,
        page: this.options.page,
        size: this.options.itemsPerPage,
        isStatsOnly: true,
        whiteListedPages: this.isSomeSelectedEventPages
          ? `${this.selectedEventPages.join(",")}`
          : null,
        sort: sortBy ? `${sortBy}${sortDesc ? ",desc" : ""}` : null,
      }

      try {
        this.loading = true

        const response = await EvercamLabsApi.posthog.getEvents(params)
        this.total = response?.total ?? 0

        this.items = response.data ?? []

        if (response?.data?.length <= 0) {
          this.headers = [...this.baseHeaders]
          this.chartData = {
            highchartsLabels: [],
            datasets: [],
          }

          return
        }

        // Extract unique dates from the data row
        let uniqueStatsDates = []
        Object.keys(response.data[0]).forEach((dateKey) => {
          if (/^\d{4}\d{2}\d{2}$/.test(dateKey)) {
            const date = `${dateKey.substring(0, 4)}-${dateKey.substring(
              4,
              6
            )}-${dateKey.substring(6, 8)}`

            uniqueStatsDates.push({
              dateKey,
              date: this.formatDate(
                date,
                this.selectedPeriod === Periods.Monthly.toLowerCase()
                  ? "MMM YYYY"
                  : "DD MMM YYYY"
              ),
            })
          }
        })
        uniqueStatsDates.pop() // Remove the last item which is the next-day column (no data)

        const periodHeaders = uniqueStatsDates.map((uniqueStatsDate) => {
          return {
            value: uniqueStatsDate.dateKey,
            text: uniqueStatsDate.date,
            visible: true,
            width: 100,
            sortable: false,
          }
        })
        this.headers = [...this.baseHeaders, ...periodHeaders]

        // Prepare chart-datasets for each data row: feature (PageId)
        const chartDatasets = response.data.map((featureRow) => {
          const featureStats = uniqueStatsDates.map((uniqueStatsDate) => {
            return featureRow[uniqueStatsDate.dateKey] || 0
          })

          return {
            label: featureRow.page.startsWith("$")
              ? featureRow.page.substring(1)
              : featureRow.page,
            data: featureStats,
            borderColor: this.getRandomColor(),
            backgroundColor: "transparent",
            fill: false,
          }
        })

        this.chartData = {
          highchartsLabels: uniqueStatsDates.map(
            (uniqueStatsDate) => uniqueStatsDate.date
          ),
          datasets: chartDatasets,
        }
      } catch (error) {
        this.$notifications.error({
          text: "Could not load Posthog Events Stats!",
          error,
        })
      } finally {
        this.loading = false
      }
    }, 1000),
    formatDate(date, format = "DD MMM YYYY HH:mm:ss") {
      return this.$moment(date).isValid()
        ? this.$moment(date).format(format)
        : "-"
    },
    getRandomColor() {
      const letters = "0123456789ABCDEF"
      let color = "#"
      for (let i = 0; i < 6; i++) {
        color += letters[Math.floor(Math.random() * 16)]
      }

      return color
    },
    isDateRangeOver30Days() {
      const start = this.$moment(this.startDate)
      const end = this.$moment(this.endDate)

      return end.diff(start, "days") > 30
    },
    async onChangeSelectedDateRange(selectedDateRange) {
      // Init date range
      let startDate = this.$moment()
      let endDate = this.$moment().format("YYYY-MM-DD")

      // Set formatted start date based on selected range
      const subtractAndFormatStartDate = (number, unit) => {
        startDate = startDate.subtract(number, unit).format("YYYY-MM-DD")
      }
      const setDateRangeMapping = {
        [DateRanges.Last24h]: () => subtractAndFormatStartDate(1, "d"),
        [DateRanges.Last7Days]: () => subtractAndFormatStartDate(7, "d"),
        [DateRanges.Last30Days]: () => subtractAndFormatStartDate(30, "d"),
        [DateRanges.Last90Days]: () => subtractAndFormatStartDate(90, "d"),
        [DateRanges.Custom]: () => {
          startDate = null
          endDate = null
          this.startEndDates = []
        },
      }
      setDateRangeMapping[selectedDateRange]()

      // Set start and end state dates
      this.startDate = startDate
      this.endDate = endDate

      // Handle range too big for daily period
      if (this.isDateRangeOver30Days()) {
        this.selectedPeriod = Periods.Weekly.toLowerCase()
      }

      // When Custom range selected
      if (selectedDateRange === DateRanges.Custom) {
        return
      }

      // When any other range selected: directly fetch data
      await this.fetchPosthogEventsStats()
    },
    onChangeSelectedCamera(selectedCameraExid) {
      this.selectedCameraExid = selectedCameraExid
      this.fetchPosthogEventsStats()
    },
    onChangeSelectedProject(selectedProjectExid) {
      this.selectedProjectExid = selectedProjectExid
      this.fetchPosthogEventsStats()
    },
    onChangeStartEndDates(dates) {
      this.startDate = dates[0]
      this.endDate = dates[1]

      // Handle range too big for daily period
      if (this.isDateRangeOver30Days()) {
        this.selectedPeriod = Periods.Weekly.toLowerCase()
      }

      this.fetchPosthogEventsStats()
    },
    toggleSelectAllselectedEventPages() {
      this.selectedEventPages = this.isAllSelectedEventPages
        ? []
        : [...this.eventPages.map((item) => item.value)]
      this.fetchPosthogEventsStats()
    },
  },
}
</script>
